import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

const FormAutofillContext = createContext(null);

// --- util: compose multiple refs into one ---
function composeRefs(...refs) {
    return (el) => {
        refs.forEach((r) => {
            if (!r) return;
            if (typeof r === 'function') r(el);
            else r.current = el;
        });
    };
}

export function FormAutofillProvider({ debounceMs = 50, children }) {
    const entriesRef = useRef(new Set());
    const debounceTimeoutRef = useRef(null);

    const checkAutofill = useCallback((reason, payload) => {
        return Array.from(entriesRef.current).reduce((result, { ref, callback }) => {
            if (ref && typeof callback === 'function') {
                return callback(ref, { type: 'autofill-detected', reason, payload }) || result;
            }
            return result;
        }, false);
    }, []);

    const triggerDebouncedNotify = useCallback((reason, payload) => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
        debounceTimeoutRef.current = setTimeout(() => checkAutofill(reason, payload), debounceMs);
    }, [debounceMs, checkAutofill]);

    useEffect(() => {
        const initialValues = new WeakMap();
        
        const snapshot = () => {
            for (const { ref } of entriesRef.current) {
                if (ref && !initialValues.has(ref)) {
                    initialValues.set(ref, ref.value ?? '');
                }
            }
        };

        const runShortRafScan = () => {
            let ticks = 0;
            const tick = () => {
                let detected = false;
                for (const { ref } of entriesRef.current) {
                    if (!ref) {
                        continue;
                    }
                    const before = initialValues.get(ref) ?? '';
                    if (!before && ref.value) {
                        detected = true;
                        break;
                    }
                }
                if (detected) {
                    triggerDebouncedNotify('pageshow/initial-rAF-scan');
                    return;
                }
                if (++ticks > 60) {
                    return; // ~300ms
                }
                requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
        };

        snapshot();
        runShortRafScan();

        const onPageShow = () => {
            snapshot();
            runShortRafScan();
        };

        window.addEventListener('pageshow', onPageShow);
        return () => {
            window.removeEventListener('pageshow', onPageShow);
        }
    }, [triggerDebouncedNotify, entriesRef]);

    useEffect(() => {
        const handleVis = () => {
            if (document.visibilityState !== 'visible') {
                return;
            }
            setTimeout(() => {
                for (const { ref } of entriesRef.current) {
                    if (ref?.value) { 
                        triggerDebouncedNotify('visibilitychange-scan');
                        break;
                    }
                }
            }, 0);
        };
        document.addEventListener('visibilitychange', handleVis);
        return () => {
            document.removeEventListener('visibilitychange', handleVis);
        }
    }, [triggerDebouncedNotify, entriesRef]);

    const value = useMemo(() => ({ entriesRef, triggerDebouncedNotify, checkAutofill }), [triggerDebouncedNotify, checkAutofill]);
    return (
        <FormAutofillContext.Provider value={value}>
            {children}
        </FormAutofillContext.Provider>
    );
}

export function useCheckAutofill() {
    const autofillContext = useContext(FormAutofillContext);
    return useMemo(() => () => autofillContext?.checkAutofill('manual'), [autofillContext])
}

export function useAutofillNotifier() {
    const ctx = useContext(FormAutofillContext);
    if (!ctx) {
        throw new Error('useAutofillNotifier must be used within a FormAutofillProvider');
    }

    const { entriesRef, triggerDebouncedNotify } = ctx;

    const onAnimationStartCapture = useCallback((e) => {
        const name = e.animationName || (e.nativeEvent && e.nativeEvent.animationName);
        if (name === 'autofill-start') {
            if (typeof e.persist === 'function') {
                e.persist();
            }
            // unified debounce + event shape
            triggerDebouncedNotify('animation', { event: e });
        }
    }, [triggerDebouncedNotify]);

    /**
     * createAutofillProps(callback, externalRef?)
     * - callback(el): called after a debounced autofill batch
     * - externalRef: optional ref from the component/parent (callback ref or object ref)
     */
    const createAutofillProps = useCallback((callback, ...externalRefs) => {
        const entry = { ref: null, callback };

        // internal ref the hook controls
        const hookRef = (el) => {
            entry.ref = el;
            if (el) entriesRef.current.add(entry);
            else entriesRef.current.delete(entry);
        };

        // compose the hook’s ref with the caller’s ref
        const ref = composeRefs(...externalRefs, hookRef);

        return { ref, onAnimationStartCapture };
    }, [entriesRef, onAnimationStartCapture]);

    return { createAutofillProps };
}